import {
  LanguageModelV4Prompt,
  SharedV4FileData,
  UnsupportedFunctionalityError,
} from "@ai-sdk/provider";

function uint8ArrayToBase64(uint8array: Uint8Array): string {
  return btoa(String.fromCharCode(...uint8array));
}

function convertDataToURL(data: SharedV4FileData, mediaType: string): string {
  switch (data.type) {
    case "url":
      return data.url.toString();

    case "data":
      return data.data instanceof Uint8Array
        ? `data:${mediaType};base64,${uint8ArrayToBase64(data.data)}`
        : `data:${mediaType};base64,${data.data}`;

    default:
      throw new UnsupportedFunctionalityError({
        functionality: `file data type: ${data.type}`,
      });
  }
}

/**
 * TransformersJS message type compatible with HuggingFace chat templates
 */
export interface TransformersMessage {
  role: string;
  content:
    | string
    | null
    | Array<{ type: string; text?: string; image?: string }>;
  tool_calls?: Array<{
    id: string;
    type: "function";
    function: {
      name: string;
      /**
       * A mapping, not a JSON string. HuggingFace chat templates serialize this
       * themselves (`arguments | tojson`), and some templates (e.g. LFM2.5)
       * reject a pre-serialized string outright.
       */
      arguments: unknown;
    };
  }>;
  tool_call_id?: string;
  name?: string;
}

/**
 * Safely normalize tool arguments - handles both string and object inputs
 */
function normalizeToolArguments(input: unknown): unknown {
  if (input === undefined) {
    return {};
  }

  if (typeof input === "string") {
    try {
      return JSON.parse(input);
    } catch {
      // If parsing fails, return the string as-is
      return input;
    }
  }

  return input ?? {};
}

function processVisionContent(
  content: any[],
): Array<{ type: "text"; text: string } | { type: "image"; image: string }> {
  const contentParts: Array<
    { type: "text"; text: string } | { type: "image"; image: string }
  > = [];
  let textParts: string[] = [];

  for (const part of content) {
    if (part.type === "text") {
      textParts.push(part.text);
    } else if (part.type === "file" && part.mediaType?.startsWith("image/")) {
      if (textParts.length > 0) {
        contentParts.push({ type: "text", text: textParts.join("\n") });
        textParts = [];
      }
      contentParts.push({
        type: "image",
        image: convertDataToURL(part.data, part.mediaType!),
      });
    } else if (part.type === "file") {
      throw new UnsupportedFunctionalityError({
        functionality: "non-image file input",
      });
    }
  }

  if (textParts.length > 0) {
    contentParts.push({ type: "text", text: textParts.join("\n") });
  }

  return contentParts;
}

export function convertToTransformersMessages(
  prompt: LanguageModelV4Prompt,
  isVisionModel: boolean = false,
): TransformersMessage[] {
  return prompt.flatMap(
    (message): TransformersMessage | TransformersMessage[] => {
      switch (message.role) {
        case "system":
          return { role: "system", content: message.content };

        case "user":
          if (isVisionModel) {
            return {
              role: "user",
              content: processVisionContent(message.content),
            };
          }

          const textContent = message.content
            .map((part) => {
              if (part.type === "text") return part.text;
              if (part.type === "file")
                throw new UnsupportedFunctionalityError({
                  functionality: "file input",
                });
              return "";
            })
            .join("\n");
          return { role: "user", content: textContent };

        case "assistant":
          const textParts = message.content
            .filter((part) => part.type === "text")
            .map((part) => (part as any).text);

          const toolCallParts = message.content.filter(
            (part) => part.type === "tool-call",
          );

          // If there are tool calls, format as HuggingFace expects
          if (toolCallParts.length > 0) {
            const tool_calls = toolCallParts.map((part) => ({
              id: (part as any).toolCallId,
              type: "function" as const,
              function: {
                name: (part as any).toolName,
                arguments: normalizeToolArguments((part as any).input),
              },
            }));

            return {
              role: "assistant",
              content: textParts.length > 0 ? textParts.join("\n") : "",
              tool_calls,
            };
          }

          return {
            role: "assistant",
            content: textParts.join("\n"),
          };

        case "tool":
          // Each tool result becomes a separate message with role "tool"
          // This is the native HuggingFace format for tool results
          return message.content
            .filter((part) => part.type === "tool-result")
            .map((part) => {
              const toolPart = part as any;

              let resultValue: unknown;
              switch (toolPart.output.type) {
                case "text":
                case "json":
                case "content":
                  resultValue = toolPart.output.value;
                  break;
                case "error-text":
                case "error-json":
                  resultValue = {
                    error: true,
                    message: toolPart.output.value,
                  };
                  break;
                case "execution-denied":
                  resultValue = {
                    error: true,
                    reason: toolPart.output.reason ?? "execution denied",
                  };
                  break;
              }

              return {
                role: "tool",
                tool_call_id: toolPart.toolCallId,
                name: toolPart.toolName,
                content:
                  typeof resultValue === "string"
                    ? resultValue
                    : JSON.stringify(resultValue),
              };
            });

        default:
          throw new Error(`Unsupported message role: ${(message as any).role}`);
      }
    },
  );
}
